// ============================================================================
// Public stipend-address collection (Hotfix Wave 1 Phase B, Task #44).
// ============================================================================
// Reached only via a tokenized link from the stipend blast:
//   #attendee-info/<43-char base64url token>
//
// Two steps, per card §3: validate the token and show the attendee's own name
// + the event title as a TRUST ANCHOR (so the recipient can tell this is
// really their link before typing an address), then the address form.
//
// The server returns an identical 404 for malformed / unknown / expired /
// consumed / archived tokens, so this renders ONE message for all of them —
// distinguishing them here would rebuild the enumeration oracle the uniform
// 404 exists to prevent.
// ============================================================================

import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, MapPin } from 'lucide-react';
import { api, type AttendeeAddress, type AttendeeInfoLookup } from '../api';

const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none';

const EMPTY: AttendeeAddress = { line1: '', line2: '', city: '', state: '', zip: '' };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        {children}
      </div>
    </div>
  );
}

export function PublicAttendeeInfoPanel({ token }: { token: string }) {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<AttendeeInfoLookup | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [form, setForm] = useState<AttendeeAddress>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Each token is evaluated independently (Copilot #16 r1). The route can
    // swap the token prop without remounting this component, and without this
    // reset a stale `invalid` would survive into a valid link — the `invalid ||
    // !info` branch wins, so a good second link would render "not valid" forever.
    setLoading(true);
    setInvalid(false);
    setInfo(null);
    setSubmitted(false);
    setErr(null);
    setForm(EMPTY);
    (async () => {
      try {
        const res = await api.attendeeInfoLookup(token);
        if (!cancelled) setInfo(res);
      } catch {
        // Uniform failure: never report WHY the link didn't work.
        if (!cancelled) setInvalid(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const update = (k: keyof AttendeeAddress) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const ready =
    form.line1.trim().length > 0 &&
    form.city.trim().length > 0 &&
    /^[A-Za-z]{2}$/.test(form.state.trim()) &&
    /^\d{5}(-\d{4})?$/.test(form.zip.trim());

  const submit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await api.attendeeInfoSubmit(token, {
        ...form,
        line2: form.line2?.trim() ? form.line2.trim() : null,
        state: form.state.trim().toUpperCase(),
      });
      setSubmitted(true);
    } catch (e: any) {
      setErr(e?.message ?? 'We could not save your address. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Shell><div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-5 h-5 animate-spin" /> Checking your link…</div></Shell>;
  }

  if (invalid || !info) {
    return (
      <Shell>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">This link isn’t valid</h1>
            <p className="mt-2 text-sm text-gray-600">
              It may have expired, or it may already have been used. Links are personal and
              work once. If you still need to send us your address, reply to the email you
              received and we’ll send a fresh link.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  if (submitted || info.attendee.has_submitted_address) {
    return (
      <Shell>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Thank you — we have your address</h1>
            <p className="mt-2 text-sm text-gray-600">
              Your stipend for <strong>{info.event.title}</strong> will be mailed there.
              Questions? Reply to the email you received or contact support@upinfarms.com.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  // timeZone: 'UTC' matches the house convention (AdminEventsPanel,
  // AdminEventAttendanceReportPanel, AdminBudgetList). Without it a workshop
  // timestamped near midnight UTC renders as the previous day for a US viewer —
  // and this page's whole job is to convince the recipient the link is really
  // theirs, so showing the wrong date undermines the trust anchor.
  const eventDate = info.event.date
    ? new Date(info.event.date).toLocaleDateString(undefined, {
        month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
      })
    : null;

  return (
    <Shell>
      {/* Trust anchor: their own name + the event, shown BEFORE any input. */}
      <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
        <MapPin className="w-6 h-6 text-uif-green shrink-0" />
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Where should we mail your stipend?</h1>
          <p className="mt-1 text-sm text-gray-600">
            {info.attendee.full_name ? <><strong>{info.attendee.full_name}</strong> — thanks for attending </> : 'Thanks for attending '}
            <strong>{info.event.title}</strong>{eventDate ? ` on ${eventDate}` : ''}.
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Street address *</span>
          <input value={form.line1} onChange={update('line1')} disabled={submitting} className={inputCls} autoComplete="address-line1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Apartment, suite, etc.</span>
          <input value={form.line2 ?? ''} onChange={update('line2')} disabled={submitting} className={inputCls} autoComplete="address-line2" placeholder="(optional)" />
        </label>
        <div className="grid grid-cols-6 gap-3">
          <label className="col-span-3 block">
            <span className="text-xs font-medium text-gray-700">City *</span>
            <input value={form.city} onChange={update('city')} disabled={submitting} className={inputCls} autoComplete="address-level2" />
          </label>
          <label className="col-span-1 block">
            <span className="text-xs font-medium text-gray-700">State *</span>
            <input value={form.state} onChange={update('state')} disabled={submitting} maxLength={2} placeholder="MS" className={inputCls + ' uppercase'} autoComplete="address-level1" />
          </label>
          <label className="col-span-2 block">
            <span className="text-xs font-medium text-gray-700">ZIP *</span>
            <input value={form.zip} onChange={update('zip')} disabled={submitting} placeholder="39201" className={inputCls} autoComplete="postal-code" />
          </label>
        </div>

        {err && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 shrink-0" /> {err}
          </div>
        )}

        <button
          type="button"
          disabled={!ready || submitting}
          onClick={() => void submit()}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-uif-green rounded-lg hover:opacity-90 disabled:opacity-50">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Send my address
        </button>
        <p className="text-xs text-gray-500">
          This link is personal to you and can be used once.
        </p>
      </div>
    </Shell>
  );
}
